import * as React from 'react';
import { Container, CssBaseline, Avatar, Typography, TextField, FormControlLabel, Checkbox, Button, Link, Box, Dialog, CircularProgress } from '@mui/material';
import { styles, withStyles } from '../settings/styles';
import { IBaseTheme } from '../settings/themes';
import Axios from 'axios';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from '../model/reducers';
import { connect } from 'react-redux';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { loadingOverlay } from '../model/overlay/actions';
import LoginAPI from '../api/login';
import qs from 'query-string';
import { WithStyles, WithTheme } from '@mui/styles';
const pjson = require('../../package.json');

// ----------------- State & Props -----------------  //

interface State 
{
    username: string;
    password: string;
    branchName: string;
    menuOpen: boolean;
    failedAuth: boolean;
    sessionEnded: boolean;
};

interface LocalProps extends WithStyles<typeof styles>, WithTheme {}

interface StateProps
{
    loading: boolean;
}

interface DispatchProps 
{
    doLoading: ()=>void;
    doneLoading: ()=>void;
}

type Props = LocalProps & StateProps & DispatchProps;

// -------------------- Class --------------------  //

class LoginPage extends React.Component<Props, State> 
{
    // ============================================ //
    //               Routine Functions              //
    // ============================================ //

    constructor(props: any)
    {
        super(props);
        
        // Init State
        this.state = {
            username: "",
            password: "",
            branchName: pjson.build.branch,
            menuOpen: false,
            failedAuth: qs.parse(window.location.search).unauthorized ? true : false,
            sessionEnded: qs.parse(window.location.search).sessionHasEnded ? true : false
        };
        this.fetchBranchName();

        this.login = this.login.bind(this);
        this.versionAndCopyright = this.versionAndCopyright.bind(this);
        this.fetchBranchName = this.fetchBranchName.bind(this);
    }

    // ============================================ //

    render () 
    {
        const { classes } = this.props;
        const theme = this.props.theme as IBaseTheme;

        let failtext = this.state.failedAuth ? (
            <Typography className={classes.login_failtext}>
                Invalid Username and/or Password
            </Typography>
        ) : this.state.sessionEnded ? (
            <Typography className={classes.login_failtext}>
                Your Session Has Ended, Please Login Again
            </Typography>
        ) : (<></>);

        return (
          <Container id="Login" component="main" maxWidth="xs" className={classes.login_base}>
            <CssBaseline />
            <div className={classes.login_paper}>
                {
                    theme.images.logoLogin === "" ? (<>
                        <Avatar className={classes.login_avatar}>
                            <LockOutlinedIcon />
                        </Avatar>
                        <Typography component="h1" variant="h5">
                            Sign In
                        </Typography>
                    </>) : <img className={classes.loginLogo} alt="login logo" src={theme.images.logoLogin} />
                }
              <form className={classes.login_form} noValidate>
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  onChange={(data=>{this.setState({username: data.target.value})})}
                />
                <TextField
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  onChange={(data=>{this.setState({password: data.target.value})})}
                  onKeyPress={(ev)=>{if(ev.key==='Enter')this.login();}}
                />
                <FormControlLabel
                  control={<Checkbox value="remember" color="primary" />}
                  label="Remember me"
                />
                <Button
                  id="login-button"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.login_submit}
                  onClick={this.login}
                >
                  Sign In
                </Button>
              </form>
              {failtext}
            </div>

            <Box mt={8}>
                {this.versionAndCopyright(this.state.branchName)}
            </Box>
            {this.renderLoadingOverlay()}
          </Container>
        );
    }

    // ============================================ //
    //               Render Functions               //
    // ============================================ //

    versionAndCopyright(branchName: string) {
        return (<>
            <Typography variant="body2" color="textSecondary" align="center">
                {"Distribution: "+branchName} {(pjson.version===""?"":"v"+pjson.version)+" "+pjson.build.number}
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center">
                {'Copyright © '}
                <Link color="inherit" href="https://viewbase.se/">
                    Viewbase
                </Link>{' '}
                {new Date().getFullYear()}
            </Typography>
        </>);
    }

    // ============================================ //

    LinkToIAM(text: string, link: string) {
        return (<>
            <Typography variant="body2" color="textSecondary" align="center">
                <Link color="primary" href={link} rel="noopener" target="_blank">
                    {text}
                </Link>
            </Typography>
        </>);
    }
        
    // ============================================ //

    renderLoadingOverlay()
    {
        return (<>
            <Dialog 
                open={this.props.loading} 
                onBackdropClick={(evt: React.SyntheticEvent<{}, Event>)=>{
                    evt.stopPropagation(); 
                    evt.preventDefault();
                }} 
                disableEscapeKeyDown 
                PaperProps={{style:{width: "100px", height: "100px"}}}
            >
                <CircularProgress style={{marginLeft: "30px", marginTop: "27px"}} />
            </Dialog>
        </>);
    }

    // ============================================ //
    //               Member Functions               //
    // ============================================ //

    login()
    {
        this.props.doLoading();
        LoginAPI.login(this.state.username, this.state.password).then((redirectUrl: string) => {
            this.props.doneLoading();
            window.location.href = redirectUrl;
        });
    }

    // ============================================ //

    fetchBranchName()
    {
        if ( pjson.build &&
             pjson.build.branch === "" )
        {
            Axios.get('./branch').then((response: any) => {
                this.setState({
                    branchName: response.data
                });
            }).catch((error: any) => {
                console.log(error);
            });
        }
    }

    // ============================================ //
}

// ----------------- Export -----------------  //

const mapStateToProps = (state: AppState, props: LocalProps): StateProps => ({
    loading: state.overlay.loading
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{},{},any>, props: LocalProps): DispatchProps => ({
    doLoading: ()=>dispatch(loadingOverlay(true)),
    doneLoading: ()=>dispatch(loadingOverlay(false))
});

export default withStyles(connect(mapStateToProps, mapDispatchToProps)(LoginPage));

// ------------------------------------------  //